import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { ChevronDown, Info, TriangleAlert } from "lucide-react";

import { cn } from "@jbnl/shared-ui";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";

/*
  The tone is a claim about the reader: `warning` means go and do something, `info` means carry
  on. Wearing warning for something nobody can act on teaches people to ignore the colour that
  matters. The icon is not optional - it is the tone said again for a reader who cannot use it.
*/
const alertVariants = cva(
  "flex items-start gap-2 rounded-md border p-3 text-sm",
  {
    variants: {
      tone: {
        info: "border-info-border bg-info-surface",
        warning: "border-warning-border bg-warning-surface",
      },
    },
    defaultVariants: { tone: "info" },
  },
);

const iconTone = {
  info: "text-info-foreground",
  warning: "text-warning-foreground",
} as const;

/** The icon carries the same meaning as the colour, for anyone who cannot use the colour. */
const toneIcon = {
  info: Info,
  warning: TriangleAlert,
} as const;

export function Alert({
  tone = "info",
  className,
  children,
  ...rest
}: React.ComponentProps<"div"> & VariantProps<typeof alertVariants>) {
  const resolved = tone ?? "info";
  const ToneIcon = toneIcon[resolved];

  return (
    // `status` rather than `alert`: these are present when the screen renders rather than
    // interrupting, so a screen reader should mention them in turn and not cut off what it is
    // already saying.
    <div
      role="status"
      className={cn(alertVariants({ tone }), className)}
      {...rest}
    >
      <ToneIcon
        aria-hidden
        className={cn("mt-0.5 size-4 shrink-0", iconTone[resolved])}
      />
      {/* Fills the banner, so a caller can push a trailing chevron to its right edge. */}
      <span className="min-w-0 flex-1">{children}</span>
    </div>
  );
}

/**
 * An `Alert` whose body is a list, folded to its title until a reader opens it.
 *
 * A season is seventeen ongedekte dagen, a VBS sync can hold back a hundred ondersteuners -
 * unfolded by default, that is a wall to scroll past before reaching whatever the screen is
 * actually for. Only the enumeration folds, never `description`: the standards' "say what to do
 * and where, not only what is wrong" is the sentence that tells a beheerder to fix it in VBS or
 * tick the boekje, and a reader who never opens the list must still read that.
 *
 * The whole banner is the control, not a chevron in the corner of it: there is nothing else on
 * this notice to click, and a target the width of the card is one a beheerder does not have to
 * aim for. Reopening it is the same gesture on the expanded list, which is why the icon does not
 * ride inside `Alert` here - that would leave it, and the tap target it sits in, outside the
 * button.
 */
export function AlertList({
  tone,
  title,
  description,
  children,
  className,
}: {
  tone: NonNullable<VariantProps<typeof alertVariants>["tone"]>;
  title: React.ReactNode;
  description?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
}) {
  const [open, setOpen] = React.useState(false);
  const ToneIcon = toneIcon[tone];

  return (
    <Collapsible
      open={open}
      onOpenChange={setOpen}
      role="status"
      className={cn(
        alertVariants({ tone }),
        "flex-col items-stretch gap-0 p-0",
        className,
      )}
    >
      {/* `type="button"`: ReistijdDialoog's gedeelde-dagen warning sits inside a form, and a
          Radix trigger has no default type of its own - left unset it falls back to the
          browser's "submit", which would save the form instead of toggling. */}
      <CollapsibleTrigger
        type="button"
        className="group flex w-full items-start gap-2 p-3 text-start text-sm"
      >
        <ToneIcon
          aria-hidden
          className={cn("mt-0.5 size-4 shrink-0", iconTone[tone])}
        />
        <span className="min-w-0 flex-1">
          <span className="flex items-center gap-2">
            <span className="min-w-0 flex-1 font-medium">{title}</span>
            {/* Bigger and tone-coloured, not the muted size-4 outline a trailing chevron
                usually gets: this one is the only sign a folded banner has anything behind it. */}
            <ChevronDown
              aria-hidden
              strokeWidth={2.5}
              className={cn(
                "size-5 shrink-0 transition-transform group-data-[state=open]:rotate-180",
                iconTone[tone],
              )}
            />
          </span>
          {description && (
            <p className="mt-1 text-muted-foreground">{description}</p>
          )}
        </span>
      </CollapsibleTrigger>
      {/* A click anywhere in the opened list closes it again, the same as the header - the
          header is the only piece wired as a `button`, so without this a beheerder who clicks
          the list itself, expecting the banner to work the way it just did, gets nothing. */}
      <CollapsibleContent
        onClick={() => setOpen(false)}
        className="cursor-pointer overflow-hidden ps-9 pe-3 pb-3 text-sm data-[state=closed]:animate-collapsible-up data-[state=open]:animate-collapsible-down"
      >
        {children}
      </CollapsibleContent>
    </Collapsible>
  );
}
